import type { HelpGuide } from '../types';

export const settingsApprovalMatrixGuide: HelpGuide = {
  key: 'settings-approval-matrix',
  title: 'เกณฑ์อนุมัติ (Approval Matrix)',
  subtitle: 'เมนู: ระบบ ▸ เกณฑ์อนุมัติ',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/settings-approval-matrix/คู่มือการใช้งาน_เกณฑ์อนุมัติ_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “เกณฑ์อนุมัติตามวงเงิน (Approval Matrix)” ใช้กำหนด “สายอนุมัติเอกสาร” ของแต่ละประเภทเอกสาร โดยแบ่งเป็น “ชั้น (Tier)” ตามวงเงิน เช่น เอกสารวงเงินน้อยให้หัวหน้าอนุมัติชั้นเดียว ส่วนวงเงินสูงต้องผ่านผู้ตรวจสอบหลายคนก่อนถึงผู้อนุมัติขั้นสุดท้าย เมื่อมีผู้ส่งเอกสารเข้าระบบ ระบบจะเลือกชั้นที่ตรงกับวงเงิน (และเงื่อนไขสาขา/หน่วยงาน) แล้ววิ่งตามสายอนุมัติที่ตั้งไว้ที่นี่โดยอัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'แนวคิดหลัก: 1 ประเภทเอกสาร = หลาย “ชั้น” แยกตามวงเงิน · แต่ละชั้นมีสายอนุมัติ = ผู้จัดทำ (ผู้ส่ง) → ผู้ตรวจสอบ (มีกี่คนก็ได้/ไม่มีก็ได้) → ผู้อนุมัติขั้นสุดท้าย (บังคับ 1 คน) · ระบบจับคู่ชั้นจาก “วงเงิน” ของเอกสาร แล้ววิ่งตามสายอนุมัติของชั้นนั้น',
        },
        {
          kind: 'para',
          text: 'ประเภทเอกสารที่ตั้งเกณฑ์ได้บนหน้านี้:',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'JV — ', text: 'ใบสำคัญทั่วไป (Journal Voucher)' },
            { lead: 'PV — ', text: 'ใบสำคัญจ่าย (Payment Voucher)' },
            { lead: 'PR — ', text: 'ใบขอเบิก/ขอจ่าย (Payment Request)' },
          ],
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'การแก้ไขหน้านี้ต้องมีสิทธิ์ approval.manage เท่านั้น และมีผลกับเอกสารที่ส่งเข้าอนุมัติหลังการบันทึก — โปรดตรวจทานสายอนุมัติให้ครบทุกชั้นก่อนกดบันทึก',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/01-overview.png',
          caption:
            'ภาพรวมเกณฑ์อนุมัติ — เลือกประเภทเอกสาร (JV) และตารางชั้นวงเงิน 3 ชั้นพร้อมสายอนุมัติของแต่ละชั้น',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'แถวบนสุด (แถบเครื่องมือ):' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ประเภทเอกสาร — ',
              text: 'เลือกประเภทเอกสารที่ต้องการตั้งเกณฑ์ (JV/PV/PR) เมื่อเปลี่ยน ตารางจะโหลดชั้นของเอกสารนั้นใหม่ทั้งหมด',
            },
            {
              lead: '+ เพิ่มชั้น — ',
              text: 'เพิ่มชั้นอนุมัติใหม่ (แถวว่างในตาราง)',
            },
            {
              lead: 'บันทึก — ',
              text: 'บันทึกชั้นทั้งหมดของประเภทเอกสารนี้ (ปุ่มสีหลัก) จะกดไม่ได้ถ้ายังมีชั้นใดไม่ได้ระบุผู้อนุมัติ',
            },
          ],
        },
        { kind: 'para', text: 'ตารางชั้นอนุมัติ มี 4 คอลัมน์:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'วงเงินสูงสุด (ว่าง = ชั้นบนสุด) — ',
              text: 'วงเงินสูงสุดที่ชั้นนี้รองรับ (ใส่ตัวเลข) — ปล่อยว่างหมายถึงชั้นบนสุด/ไม่จำกัด',
            },
            {
              lead: 'เงื่อนไข สาขา / หน่วยงาน (ว่าง = ทุกหน่วยงาน) — ',
              text: 'จำกัดให้ชั้นนี้ใช้เฉพาะสาขา/หน่วยงานที่เลือก — ปล่อยว่างหมายถึงใช้ได้ทั้งบริษัท',
            },
            {
              lead: 'สายอนุมัติ: ผู้ตรวจสอบ → ผู้อนุมัติ — ',
              text: 'ตัวสร้างสายอนุมัติของชั้นนั้น (ผู้จัดทำ/ผู้ตรวจสอบ/ผู้อนุมัติ)',
            },
            {
              lead: 'ปุ่ม ✕ — ',
              text: 'ลบชั้นที่แถวนั้นออก',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ด้านล่างตารางมีการ์ด “ทดสอบ (preview)” ไว้ลองใส่จำนวนเงิน (และสาขา/หน่วยงาน) เพื่อดูว่าระบบจะเลือกสายอนุมัติชั้นไหนให้',
        },
      ],
    },
    {
      id: 'select-doctype',
      heading: '3. เลือกประเภทเอกสาร และอ่านตารางชั้นอนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'เริ่มจากเลือก ประเภทเอกสาร ที่มุมบนซ้าย ระบบจะแสดงชั้นอนุมัติทั้งหมดของเอกสารนั้น เรียงตามวงเงิน โดยชั้นที่มีวงเงินน้อยอยู่บน และชั้น “ไม่จำกัด” (วงเงินว่าง) คือชั้นบนสุดสำหรับเอกสารวงเงินสูง',
        },
        {
          kind: 'para',
          text: 'อ่านตารางจากซ้ายไปขวา: วงเงินสูงสุดของชั้น → เงื่อนไขสาขา/หน่วยงาน (ถ้ามี) → สายอนุมัติของชั้นนั้น แต่ละชั้นทำงานเป็นอิสระ ระบบจะหยิบใช้เพียงชั้นเดียวที่ตรงกับวงเงินและเงื่อนไขของเอกสารที่ส่งเข้ามา',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/02-doctype-tiers.png',
          caption: 'เลือกประเภทเอกสารแล้วระบบแสดงตารางชั้นอนุมัติ เรียงตามวงเงินสูงสุดของแต่ละชั้น',
        },
      ],
    },
    {
      id: 'chain-basic',
      heading: '4. สายอนุมัติของชั้น — ผู้จัดทำ / ผู้ตรวจสอบ / ผู้อนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'ในช่อง “สายอนุมัติ” ของแต่ละชั้น มี 3 ส่วน:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ผู้จัดทำ (maker) — ',
              text: 'คือผู้ส่งอนุมัติเอกสาร ระบบกำหนดให้เอง ไม่นับเป็นขั้นในสายอนุมัติและแก้ไขไม่ได้ (แสดงไว้เพื่ออ้างอิงเท่านั้น)',
            },
            {
              lead: 'ผู้ตรวจสอบ (reviewers) — ',
              text: 'ขั้นกลางที่ต้องตรวจก่อนถึงผู้อนุมัติ จะมีกี่คนก็ได้หรือไม่มีเลยก็ได้ (ถ้าว่างจะแสดง “— ไม่มีผู้ตรวจสอบ —”)',
            },
            {
              lead: 'ผู้อนุมัติ (approver) — ',
              text: 'ผู้อนุมัติขั้นสุดท้าย บังคับต้องมี 1 คนเสมอ (มีเครื่องหมาย * และกรอบสีแดงถ้ายังไม่เลือก)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'บรรทัด “ลำดับ:” ใต้สายอนุมัติจะสรุปลำดับบทบาททั้งหมดให้เห็นเป็น CODE1 → CODE2 → CODE3 (จากผู้ตรวจสอบไปยังผู้อนุมัติขั้นสุดท้าย)',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/03-chain-builder-row.png',
          caption:
            'สายอนุมัติของชั้นแรก — ส่วนผู้จัดทำ (อ้างอิง), ผู้ตรวจสอบ และผู้อนุมัติขั้นสุดท้าย',
        },
      ],
    },
    {
      id: 'chain-reviewer-approver',
      heading: '5. การกำหนดผู้ตรวจสอบและผู้อนุมัติ',
      blocks: [
        {
          kind: 'steps',
          items: [
            {
              title: 'เพิ่มผู้ตรวจสอบ',
              text: 'เลือกบทบาทจากเมนู “+ เพิ่มผู้ตรวจสอบ…” ระบบจะเพิ่มเข้าไปในรายการตามลำดับ (1, 2, 3, …) — กด × ข้างชื่อเพื่อเอาออก',
            },
            {
              title: 'เลือกผู้อนุมัติขั้นสุดท้าย',
              text: 'เลือกบทบาทจากเมนู “— เลือกผู้อนุมัติขั้นสุดท้าย * —” (บังคับ)',
            },
            {
              title: 'ตรวจลำดับ',
              text: 'ดูบรรทัด “ลำดับ:” ว่าเรียงจากผู้ตรวจสอบไปยังผู้อนุมัติถูกต้องตามที่ต้องการ',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'ลำดับของผู้ตรวจสอบมีผลจริง — เอกสารจะถูกส่งให้ผู้ตรวจสอบคนที่ 1 ก่อน เมื่อผ่านแล้วจึงไปคนที่ 2 และคนถัด ๆ ไป ก่อนถึงผู้อนุมัติขั้นสุดท้าย',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/04-chain-reviewer-approver.png',
          caption: 'ชั้นที่มีผู้ตรวจสอบ + ผู้อนุมัติขั้นสุดท้าย และบรรทัดลำดับสายอนุมัติที่สรุปให้',
        },
      ],
    },
    {
      id: 'add-tier',
      heading: '6. การเพิ่มชั้นอนุมัติใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + เพิ่มชั้น เพื่อเพิ่มแถวว่างในตาราง แล้วกรอก วงเงินสูงสุด ของชั้นนั้น (เช่น 100000) ตั้งเงื่อนไขสาขา/หน่วยงานถ้าต้องการ จากนั้นสร้างสายอนุมัติ (ผู้ตรวจสอบ + ผู้อนุมัติ) ของชั้น',
        },
        {
          kind: 'para',
          text: 'แนะนำให้ตั้งหลายชั้นแยกตามวงเงิน เช่น ชั้นวงเงิน 100,000 / 500,000 และอีกชั้นปล่อยวงเงินว่าง (ไม่จำกัด) สำหรับเอกสารวงเงินสูงสุด ระบบจะเรียงชั้นตามวงเงินให้อัตโนมัติ',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/05-add-tier.png',
          caption:
            'เพิ่มชั้นอนุมัติใหม่ — แถวว่างพร้อมช่องวงเงินสูงสุด เงื่อนไขสาขา/หน่วยงาน และสายอนุมัติ',
        },
      ],
    },
    {
      id: 'validation',
      heading: '7. การเตือนเมื่อชั้นยังไม่สมบูรณ์',
      blocks: [
        {
          kind: 'para',
          text: 'ทุกชั้น “ต้อง” ระบุผู้อนุมัติขั้นสุดท้ายอย่างน้อย 1 คน หากมีชั้นใดยังไม่ได้เลือกผู้อนุมัติ ช่องผู้อนุมัติจะขึ้นกรอบสีแดง ระบบจะแสดงข้อความเตือนสีแดงว่า “ทุกชั้นต้องระบุผู้อนุมัติ (ผู้อนุมัติขั้นสุดท้าย) อย่างน้อย 1 คน” และปุ่ม บันทึก จะถูกปิดไว้',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'เมื่อเลื่อนเมาส์ที่ปุ่มบันทึกที่กดไม่ได้ จะเห็นคำอธิบาย “ทุกชั้นต้องมีผู้อนุมัติ (ผู้อนุมัติคนสุดท้าย)” — แก้โดยเลือกผู้อนุมัติให้ครบทุกชั้นก่อน',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/06-validation-error.png',
          caption:
            'การเตือนเมื่อมีชั้นยังไม่ระบุผู้อนุมัติ — กรอบแดง + ข้อความเตือน และปุ่มบันทึกถูกปิด',
        },
      ],
    },
    {
      id: 'edit-tier',
      heading: '8. การแก้ไขชั้นเดิม (เพิ่ม/ลดผู้ตรวจสอบ)',
      blocks: [
        {
          kind: 'para',
          text: 'หากต้องการเพิ่มขั้นตรวจสอบให้ชั้นที่มีอยู่ ให้เลือกบทบาทเพิ่มจากเมนู “+ เพิ่มผู้ตรวจสอบ…” ในชั้นนั้น หรือกด × เพื่อเอาผู้ตรวจสอบที่ไม่ต้องการออก สามารถปรับวงเงินสูงสุด เงื่อนไขสาขา/หน่วยงาน และเปลี่ยนผู้อนุมัติได้ทุกเมื่อ เมื่อแก้เสร็จอย่าลืมกด บันทึก',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/07-edit-add-reviewer.png',
          caption: 'แก้ไขชั้นเดิม — เพิ่มผู้ตรวจสอบเข้าไปในสายอนุมัติของชั้นที่มีอยู่',
        },
      ],
    },
    {
      id: 'preview',
      heading: '9. ทดสอบ/พรีวิวสายอนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด “ทดสอบ (preview)” ใต้ตาราง ใช้ลองดูว่าระบบจะเลือกสายอนุมัติชั้นไหนให้กับเอกสารหนึ่ง ๆ ใส่ จำนวนเงิน (เช่น 300000) และถ้าต้องการ ให้เลือก สาขา/หน่วยงาน (ไม่บังคับ) ระบบจะแสดงสายอนุมัติที่ตรงกันเป็นลำดับ เช่น “1. ผู้ตรวจสอบ → 2. ผู้อนุมัติ” ถ้าไม่ตรงชั้นใดเลยจะแสดง “—”',
        },
        {
          kind: 'callout',
          text: 'ใช้พรีวิวตรวจสอบก่อนบันทึกทุกครั้ง โดยลองใส่วงเงินหลาย ๆ ค่า (เช่น ต่ำกว่า/สูงกว่าเส้นวงเงินของแต่ละชั้น) เพื่อยืนยันว่าเอกสารแต่ละขนาดจะวิ่งตามสายอนุมัติที่ถูกต้อง',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/08-preview-test.png',
          caption:
            'การ์ดทดสอบ (preview) — ใส่จำนวนเงินแล้วระบบแสดงสายอนุมัติที่จะถูกเลือกตามวงเงิน',
        },
      ],
    },
    {
      id: 'switch-doctype',
      heading: '10. การสลับดูเกณฑ์ของเอกสารอีกประเภท',
      blocks: [
        {
          kind: 'para',
          text: 'เปลี่ยนค่าในช่อง ประเภทเอกสาร เพื่อตั้ง/ดูเกณฑ์ของเอกสารอีกประเภท เช่น สลับจาก JV เป็น PV (ใบสำคัญจ่าย) เมื่อสลับ ฟอร์มจะโหลดใหม่และแสดงชั้นอนุมัติของประเภทนั้นแทน (อาจว่างถ้ายังไม่เคยตั้ง) แต่ละประเภทเอกสารมีชุดชั้นอนุมัติของตัวเองแยกจากกัน',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'การบันทึกมีผลเฉพาะประเภทเอกสารที่กำลังเปิดอยู่ ควรกด บันทึก ให้เรียบร้อยก่อนสลับไปประเภทอื่น มิฉะนั้นการแก้ไขที่ยังไม่บันทึกอาจหาย',
        },
        {
          kind: 'image',
          src: '/help/settings-approval-matrix/09-doctype-switch-pv.png',
          caption: 'สลับดูเกณฑ์ของเอกสารประเภท PV (ใบสำคัญจ่าย) — ฟอร์มโหลดชั้นอนุมัติของ PV ใหม่',
        },
      ],
    },
    {
      id: 'fields',
      heading: '11. สรุปความหมายของช่องในแต่ละชั้น',
      blocks: [
        {
          kind: 'table',
          head: ['ช่อง', 'ความหมาย / การใช้งาน'],
          rows: [
            [
              'วงเงินสูงสุด',
              'วงเงินสูงสุดที่ชั้นนี้รองรับ — ปล่อยว่าง = ชั้นบนสุด/ไม่จำกัด (มีได้ชั้นเดียว)',
            ],
            [
              'เงื่อนไขสาขา / หน่วยงาน',
              'จำกัดให้ชั้นนี้ใช้เฉพาะสาขา/หน่วยงานที่เลือก — ว่าง = ใช้ได้ทั้งบริษัท',
            ],
            ['ผู้จัดทำ (maker)', 'ผู้ส่งเอกสาร ระบบกำหนดเอง ไม่นับเป็นขั้นและแก้ไขไม่ได้'],
            ['ผู้ตรวจสอบ (reviewers)', 'ขั้นกลางก่อนอนุมัติ มีกี่คนก็ได้/ไม่มีก็ได้ ลำดับมีผลจริง'],
            ['ผู้อนุมัติ (approver)', 'ผู้อนุมัติขั้นสุดท้าย บังคับมี 1 คนทุกชั้น'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมกดปุ่ม “บันทึก” ไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เพราะมีบางชั้นยังไม่ได้เลือกผู้อนุมัติขั้นสุดท้าย — เลือกผู้อนุมัติให้ครบทุกชั้น (ช่องที่ขึ้นกรอบแดง) แล้วปุ่มจะกดได้',
            },
          ],
        },
        { kind: 'para', text: 'ปล่อยช่องวงเงินสูงสุดว่างหมายความว่าอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หมายถึงชั้นบนสุด/ไม่จำกัดวงเงิน ใช้รองรับเอกสารที่วงเงินสูงเกินทุกชั้นอื่น',
            },
          ],
        },
        { kind: 'para', text: 'ตั้งสายอนุมัติได้กี่คน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ผู้ตรวจสอบจะมีกี่คนก็ได้หรือไม่มีเลย แต่ผู้อนุมัติขั้นสุดท้ายต้องมี 1 คนเสมอ',
            },
          ],
        },
        { kind: 'para', text: 'แก้ JV แล้วเกณฑ์ของ PV เปลี่ยนตามไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ — แต่ละประเภทเอกสารมีชุดชั้นอนุมัติของตัวเอง การบันทึกมีผลเฉพาะประเภทที่กำลังเปิดอยู่',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ชั้น (Tier) — ',
              text: 'ระดับของเกณฑ์อนุมัติที่แบ่งตามวงเงิน แต่ละชั้นมีสายอนุมัติของตัวเอง',
            },
            {
              lead: 'สายอนุมัติ (Chain) — ',
              text: 'ลำดับผู้เกี่ยวข้องในการอนุมัติ: ผู้จัดทำ → ผู้ตรวจสอบ → ผู้อนุมัติ',
            },
            {
              lead: 'ผู้จัดทำ (Maker) — ',
              text: 'ผู้ส่งเอกสารเข้าอนุมัติ ไม่นับเป็นขั้นในสายอนุมัติ',
            },
            {
              lead: 'ผู้ตรวจสอบ (Reviewer) — ',
              text: 'ผู้ตรวจขั้นกลางก่อนถึงผู้อนุมัติขั้นสุดท้าย',
            },
            {
              lead: 'ผู้อนุมัติ (Approver) — ',
              text: 'ผู้อนุมัติขั้นสุดท้ายของชั้น บังคับต้องมี',
            },
            {
              lead: 'วงเงินสูงสุด (maxAmount) — ',
              text: 'เพดานวงเงินที่ชั้นรองรับ ว่าง = ไม่จำกัด/ชั้นบนสุด',
            },
            {
              lead: 'JV / PV / PR — ',
              text: 'ใบสำคัญทั่วไป / ใบสำคัญจ่าย / ใบขอเบิก-ขอจ่าย',
            },
          ],
        },
      ],
    },
  ],
};
